import React, { useState } from 'react';
import { useEditor } from '../contexts/EditorContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import { ImageElement } from '../types/elements';

const MAX_DIM = 1000; // أقصى بعد افتراضي للصورة
const DEFAULT_IMAGE = 'https://bshosimfjlunrwmwbgzt.supabase.co/storage/v1/object/public/project-images/default-image.svg';

const AddImageElement: React.FC = () => {
  const { addElement, elements } = useEditor();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [src, setSrc] = useState('');
  const [width, setWidth] = useState<number | ''>('');
  const [height, setHeight] = useState<number | ''>('');
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);

  const getNextName = () => {
    let idx = 1;
    const base = 'img-';
    while (elements.some(e => e.name === base + idx)) idx++;
    return base + idx;
  };
  const [name, setName] = useState(getNextName());

  const handleAdd = () => {
    // إذا لم يكن هناك رابط، استخدم الصورة الافتراضية
    const imageSrc = src || DEFAULT_IMAGE;
    const id = Date.now().toString();
    const order = elements.length;
    // يضمن أن الـ name يبدأ بـ img-
    let finalName = name.replace(/[^a-zA-Z0-9\-_]/g, '');
    if (!finalName.startsWith('img-')) {
      finalName = 'img-' + finalName.replace('img-', '');
    }
    if (!finalName || finalName === 'img-') finalName = getNextName();
    const newImage: ImageElement = {
      id,
      type: 'image',
      order,
      x: 50,
      y: 50 + order * 40,
      src: imageSrc,
      width: typeof width === 'number' ? width : 300,
      height: typeof height === 'number' ? height : 300,
      name: finalName,
      fileName: fileName ? fileName.replace(/[^a-zA-Z0-9\-_\.]/g, '') : undefined,
      objectFit: 'cover', // القيمة الافتراضية
    };
    addElement(newImage);
    setSrc('');
    setFileName('');
    setWidth('');
    setHeight('');
    setName(getNextName());
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setFileName(file.name);
    setUploading(true);
    
    try {
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}/${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('project-images')
        .upload(fileName, file);
      
      if (error) {
        console.warn('Upload to storage failed, using data URI instead:', error);
        // Fallback to data URI if upload fails
        const reader = new FileReader();
        reader.onload = (ev) => {
          const img = new window.Image();
          img.onload = () => {
            let w = img.width;
            let h = img.height;
            if (w > MAX_DIM || h > MAX_DIM) {
              const scale = Math.min(MAX_DIM / w, MAX_DIM / h);
              w = Math.round(w * scale);
              h = Math.round(h * scale);
            }
            setWidth(w);
            setHeight(h);
            setSrc(ev.target?.result as string);
            setUploading(false);
          };
          img.src = ev.target?.result as string;
        };
        reader.readAsDataURL(file);
      } else {
        // Use public URL
        const { data: urlData } = supabase.storage
          .from('project-images')
          .getPublicUrl(fileName);
        
        const img = new window.Image();
        img.onload = () => {
          let w = img.width;
          let h = img.height;
          if (w > MAX_DIM || h > MAX_DIM) {
            const scale = Math.min(MAX_DIM / w, MAX_DIM / h);
            w = Math.round(w * scale);
            h = Math.round(h * scale);
          }
          setWidth(w);
          setHeight(h);
          setSrc(urlData.publicUrl);
          setUploading(false);
        };
        img.src = urlData.publicUrl;
      }
    } catch (err) {
      console.error('Error handling image:', err);
      setUploading(false);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <h3 style={{ color: '#fff', marginBottom: 12, fontSize: 18 }}>{t('components.addImage.title')}</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexDirection: 'column' }}>
        <input 
          value={src} 
          onChange={e => setSrc(e.target.value)} 
          style={{ width: '100%', background: 'rgba(0, 0, 0, 0.3)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 8, padding: '8px' }} 
          placeholder={t('components.addImage.urlPlaceholder')} 
          disabled={!!fileName} 
        />
        <label style={{ 
          display: 'block', 
          width: '100%', 
          background: 'rgba(42, 42, 42, 0.5)', 
          color: '#a1a1aa', 
          border: '1px solid rgba(255, 255, 255, 0.1)', 
          borderRadius: 8, 
          padding: '8px', 
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'rgba(30, 72, 224, 0.2)';
          e.currentTarget.style.borderColor = '#1e48e0';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'rgba(42, 42, 42, 0.5)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
        }}>
          {fileName || t('components.addImage.selectFile')}
          <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
        </label>
        {fileName && <div style={{ fontSize: 12, color: '#22c55e' }}>{t('components.addImage.fileSelected', { fileName })}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="number" value={width} min={10} max={2000} onChange={e => setWidth(Number(e.target.value))} style={{ flex: 1, background: 'rgba(0, 0, 0, 0.3)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 8, padding: '8px' }} placeholder={t('components.addImage.width')} />
          <input type="number" value={height} min={10} max={2000} onChange={e => setHeight(Number(e.target.value))} style={{ flex: 1, background: 'rgba(0, 0, 0, 0.3)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 8, padding: '8px' }} placeholder={t('components.addImage.height')} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#a1a1aa' }}>{t('components.addImage.elementNameLabel')}</label>
          <input value={name} onChange={e => setName(e.target.value.replace(/[^a-zA-Z0-9\-_]/g, ''))} style={{ width: '100%', background: 'rgba(0, 0, 0, 0.3)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 8, padding: '8px' }} placeholder="img1" required />
        </div>
      </div>
      <button 
        onClick={handleAdd} 
        disabled={uploading}
        style={{ 
          width: '100%', 
          background: uploading ? '#6b7280' : '#1e48e0', 
          color: '#fff', 
          border: 'none', 
          borderRadius: 8, 
          padding: '10px', 
          fontWeight: 700, 
          cursor: uploading ? 'not-allowed' : 'pointer', 
          transition: 'background 0.2s',
          opacity: uploading ? 0.7 : 1
        }} 
        onMouseEnter={e => !uploading && (e.currentTarget.style.background = '#163aa8')} 
        onMouseLeave={e => !uploading && (e.currentTarget.style.background = '#1e48e0')}
      >
        {uploading ? t('components.addImage.uploading') : (src || fileName ? t('components.addImage.addButton') : t('components.addImage.addDefault'))}
      </button>
    </div>
  );
};

export default AddImageElement;
